import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { OrgAnnouncements } from '@app/orgs/services/org-announcements.service';
import { AuthService } from '@app/shared/services/auth.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { WebEnvironmentService } from '@app/shared/services/web-environment.service';
import { ThumbnailService } from '@app/thumbnails/thumbnail.service';
import { LDFlagsService } from '@dg/shared-services';
import { TranslateService } from '@ngx-translate/core';

// When in "preview" mode the tracking and dismiss functionality will be disabled and a preview
// org announcement will be shown when blank.
export type Mode = 'live' | 'preview';

@Component({
  selector: 'dgx-org-announcements',
  templateUrl: './org-announcements.component.html',
  styleUrls: ['./org-announcements.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrgAnnouncementsComponent implements OnChanges {
  @Input() public mode: Mode = 'live';
  @Input() public isOnSettingsPageV2?: boolean = false;
  @Input() public orgAnnouncements: OrgAnnouncements;
  @Input() public showDismiss = true;

  @Output() public dismissEvent: EventEmitter<void> = new EventEmitter();
  public i18n = this.translateService.instant([
    'OrgAnnouncement_AddALink',
    'OrgAnnouncement_BlankDescription',
    'Core_Announcement',
    'Core_Close',
    'OrgAnnouncement_Label',
  ]);
  public isEmpty: boolean;
  public imgSrc: string;
  public org = this.authService.authUser.defaultOrgInfo;
  public useIllustration: boolean = false;
  public defaultTitle = `${this.org.name} ${this.i18n.Core_Announcement}`;
  constructor(
    private authService: AuthService,
    private webEnvironmentService: WebEnvironmentService,
    private thumbnailService: ThumbnailService,
    private trackerService: TrackerService,
    private translateService: TranslateService,
    private ldFlagsService: LDFlagsService,
  ) {}

  private get trackingProperties() {
    return this.orgAnnouncements
      ? {
          linkUrl: this.orgAnnouncements.linkUrl,
          linkTitle: this.orgAnnouncements.linkName,
          title: this.orgAnnouncements.title,
          description: this.orgAnnouncements.description,
        }
      : {};
  }

  public get useLearnerHub() {
    return this.ldFlagsService.useLearnerHub;
  }

  public ngOnChanges(): void {
    if (
      this.mode === 'preview' &&
      this.isBlankOrgAnnouncement(this.orgAnnouncements)
    ) {
      this.orgAnnouncements = this.getBlankOrgAnnouncement();
    }
    this.imgSrc = this.getImageUrl();
  }

  public getImageUrl() {
    // use the image on the orgAnnouncement. If that is empty use the org logo.
    // If there is no org logo use the default illustration
    let imageUrl = this.orgAnnouncements?.imageUrl;
    // use org image if user is not using the new layout
    if (!imageUrl && !this.useLearnerHub) {
      imageUrl = this.org?.image;
    }

    if (imageUrl) {
      const imageSrc = this.webEnvironmentService.getBlobUrl(imageUrl);
      this.useIllustration = false;
      return this.thumbnailService.fetchProxyImageSrcset({
        imageSrc,
        imageWidth: 86,
        imageHeight: 54,
        method: 'fetch',
        crop: 'fill',
      }).retina;
    } else if (!imageUrl && !this.useLearnerHub) {
      this.useIllustration = true;
      const defaultIllustration = '/content/img/default-org-announcement.png';
      return this.webEnvironmentService.getBlobUrl(defaultIllustration, true);
    }
  }

  public dismiss() {
    if (!this.isPreviewMode()) {
      this.trackerService.trackEventData({
        action: 'Org announcement dismissed',
        properties: this.trackingProperties,
      });

      this.dismissEvent.emit();
    }
  }

  public linkClicked() {
    if (!this.isPreviewMode()) {
      this.trackerService.trackEventData({
        action: 'Org announcement clicked',
        properties: this.trackingProperties,
      });
    }
  }

  private isBlankOrgAnnouncement(orgAnnouncements: OrgAnnouncements) {
    return (
      !orgAnnouncements ||
      (!orgAnnouncements.title &&
        !orgAnnouncements.description &&
        !orgAnnouncements.linkName &&
        !orgAnnouncements.linkUrl)
    );
  }

  private getBlankOrgAnnouncement(): OrgAnnouncements {
    return {
      title: this.defaultTitle,
      description: this.i18n.OrgAnnouncement_BlankDescription,
      linkName: this.i18n.OrgAnnouncement_AddALink,
      linkUrl: '/',
      imageUrl: null,
      enabled: false,
    };
  }

  private isPreviewMode() {
    return this.mode === 'preview';
  }
}
